import { Box } from '@mui/material';
import { KeyboardEvent, useCallback, useMemo } from 'react';
import { createEditor, Descendant } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, Slate, withReact } from 'slate-react';
import { Element, Leaf, Toolbar } from './components';
import { withImages, withLinks } from './plugins';
import { withEjs } from './plugins/withEjs';
import { CustomEditor, CustomElements, CustomText, RenderElementProps, RenderLeafProps } from './types';
import { handleBreakoutEjsBlocks, onHotKey } from './utils';

// Custom types required for Slate
declare module 'slate' {
	interface CustomTypes {
		Editor: CustomEditor;
		Element: CustomElements;
		Text: CustomText;
	}
}

/**
 * Future TODO:
 *
 * - Uploading of images
 * - Attachments
 */

// Slate based text editor
type EditorProps = {
	value: Descendant[];
	onChange: (value: Descendant[]) => void;
};
const NewsletterEditor = (props: EditorProps) => {
	// Slate
	const editor = useMemo(() => {
		return withImages(
			withLinks(withEjs(withHistory(withReact(createEditor()))))
		);
	}, []);

	// Editable
	const renderElement = useCallback((props: RenderElementProps) => <Element {...props} />, []);
	const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, []);

	const onKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
		onHotKey(event, editor);
		handleBreakoutEjsBlocks(event, editor);
	}, []);

	return (
		<Slate
			editor={editor}
			initialValue={props.value}
			onChange={props.onChange}
		>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
				<Toolbar />
				<Box
					sx={{
						border: (theme) => `1px solid ${theme.palette.divider}`,
						borderRadius: 2,
						backgroundColor: 'background.paper',
						p: 2,
						minHeight: 280,
						'& [contenteditable]': { outline: 'none' },
						// The editor draws the email on the app's surface, so
						// links need the app's link colour to stay legible in
						// dark mode. The sent mail is unaffected - that is
						// rendered from the template in the preview iframe.
						'& a': { color: (theme) => theme.palette.info.main },
					}}
				>
					<Editable
						placeholder='Text hier eingeben...'
						renderElement={renderElement}
						renderLeaf={renderLeaf}
						autoFocus
						onKeyDown={onKeyDown}
					/>
				</Box>
			</Box>
		</Slate>
	);
};

export default NewsletterEditor;
